export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Which `data.*` field addresses a layout bundle. `matchNumber` for match
 * templates (MULTIPLE_SCHEDULE/RESULTS), `teamNumber` (table position) for
 * standings templates (TOURNAMENT_TABLE), `playerNumber` (rank position) for
 * the player-statistics template.
 *
 * Note `playerNumber` is overloaded across template types: on a match template
 * it is a roster index and is NOT a layout scope; only a player-statistics
 * render ever passes it here. `layoutScopeKeyFor` (template-type registry) and
 * `scopeOf` (run-render-pipeline) are the two places that decide, and they must
 * agree.
 */
export type LayoutScopeKey = 'matchNumber' | 'teamNumber' | 'playerNumber';

interface AuthoredFabricObject {
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  scaleX?: number;
  scaleY?: number;
  originX?: 'left' | 'center' | 'right' | string;
  originY?: 'top' | 'center' | 'bottom' | string;
  data?: Record<string, number | undefined> | undefined;
}

/**
 * Bounding rect of one authored object, derived from its saved fabric.js JSON
 * fields. Honors originX/originY so the rect matches what fabric would render.
 * Rotation is treated as axis-aligned — multi-match authored bundles are not
 * expected to be rotated; if a member is, the rect is the un-rotated AABB.
 */
export function objectBbox(obj: AuthoredFabricObject): Rect {
  const width = (obj.width ?? 0) * (obj.scaleX ?? 1);
  const height = (obj.height ?? 0) * (obj.scaleY ?? 1);
  const originX = obj.originX ?? 'left';
  const originY = obj.originY ?? 'top';
  let left = obj.left ?? 0;
  let top = obj.top ?? 0;
  if (originX === 'center') left -= width / 2;
  else if (originX === 'right') left -= width;
  if (originY === 'center') top -= height / 2;
  else if (originY === 'bottom') top -= height;
  return { left, top, width, height };
}

export function unionRects(rects: readonly Rect[]): Rect | undefined {
  if (rects.length === 0) return undefined;
  let minL = Infinity;
  let minT = Infinity;
  let maxR = -Infinity;
  let maxB = -Infinity;
  for (const r of rects) {
    if (r.left < minL) minL = r.left;
    if (r.top < minT) minT = r.top;
    if (r.left + r.width > maxR) maxR = r.left + r.width;
    if (r.top + r.height > maxB) maxB = r.top + r.height;
  }
  return { left: minL, top: minT, width: maxR - minL, height: maxB - minT };
}

/**
 * Aggregate bbox of every authored object that carries a positive
 * `data[scopeKey]` (`matchNumber` for matches, `teamNumber` for standings).
 * Objects with the scope field 0/negative/undefined are ignored — they're
 * slot-agnostic and don't define the slot region.
 */
export function authoredBundlesBbox(
  authored: readonly unknown[],
  scopeKey: LayoutScopeKey,
): Rect | undefined {
  const rects: Rect[] = [];
  for (const raw of authored) {
    const obj = raw as AuthoredFabricObject;
    const n = obj?.data?.[scopeKey];
    if (typeof n !== 'number' || n < 1) continue;
    rects.push(objectBbox(obj));
  }
  return unionRects(rects);
}
